// src/modules/helpers/item.js
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { IonIcon } from "@ionic/react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  reload,
  chevronDown,
  chevronForward,
  trash,
  eyeOutline,
  eyeOffOutline
} from "ionicons/icons";
import ErrorModal from "./ErrorModal";
import MillingDropdown from "./MillingSelector";
import { useLanguage } from "./LanguageContext";
import "../../css/Item.css";

export default function Item({
  calledFrom,
  Item: initialItem,
  onColorClick,
  onItemChange,
  onDelete,
  visible = true,
  onToggleVisibility,
  readOnly = false,
  color = null,
  checked = false,
  onCheckedChange
}) {
  const { t } = useLanguage();

  const allMillings = useSelector((state) => state.millings || []);
  const allColors = useSelector((state) =>
    Array.isArray(state.colors)
      ? state.colors
      : Object.values(state.colors || {})
  );

  function parseStringToArray(val) {
    if (Array.isArray(val)) {
      return val.map(Number);
    }

    if (typeof val === "string") {
      try {
        const arr = JSON.parse(val);

        if (Array.isArray(arr)) {
          return arr.map(Number);
        }
      } catch {}

      return [...String(val).matchAll(/-?\d+(\.\d+)?/g)].map((m) =>
        Number(m[0])
      );
    }

    return [0, 0, 0];
  }

  function parseBracketString(str) {
    if (!str) {
      return ["", "0", "0"];
    }

    const parts = str
      .slice(1, -1)
      .split(",")
      .map((s) => s.trim());

    return parts.length === 3 ? parts : ["", "0", "0"];
  }

  const [item, setItem] = useState(() => {
    const sizeArr = parseStringToArray(initialItem.size);
    const [cantType, longCant, shortCant] = parseBracketString(
      initialItem.kant
    );

    return {
      ...initialItem,
      sizeArr,
      cantType,
      longCant,
      shortCant,
      millingId: initialItem.milling?.millingId ?? initialItem.millingId ?? null
    };
  });

  const [showError, setShowError] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    const sizeArr = parseStringToArray(initialItem.size);
    const [ct, lc, sc] = parseBracketString(initialItem.kant);

    setItem({
      ...initialItem,
      sizeArr,
      cantType: ct,
      longCant: lc,
      shortCant: sc,
      millingId: initialItem.milling?.millingId ?? initialItem.millingId ?? null
    });
  }, [initialItem]);

  const validateForm = () => {
    if (
      item.sizeArr.length !== 3 ||
      item.sizeArr.some((v) => isNaN(v) || v <= 0)
    ) {
      return false;
    }

    if (item.qty === "" || isNaN(item.qty) || item.qty <= 0) {
      return false;
    }

    if (isNaN(Number(item.longCant)) || isNaN(Number(item.shortCant))) {
      return false;
    }

    return true;
  };

  const commitIfValid = () => {
    if (readOnly) return;

    if (validateForm()) {
      setShowError(false);
      onItemChange(item);
    } else {
      setShowError(true);
    }
  };

  const handleChange = (field, value) => {
    if (readOnly) return;

    let updated = { ...item };

    if (field === "sizeArr") {
      const nums = value.map((v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      });

      updated.sizeArr = nums;
      updated.size = JSON.stringify(nums);
    } else {
      updated[field] = value;
    }

    if (["cantType", "longCant", "shortCant"].includes(field)) {
      updated.kant = `[${updated.cantType},${updated.longCant},${updated.shortCant}]`;
    }

    setItem(updated);
  };

  const handleMillingChange = (patch) => {
    if (readOnly) return;

    const updated = { ...item, ...patch };
    if ("colorId" in patch) {
      updated.color = allColors.find((entry) => String(entry.colorId) === String(patch.colorId)) || null;
    }
    setItem(updated);

    if (validateForm()) {
      setShowError(false);
      onItemChange(updated);
    }
  };

  const handleRotate = () => {
    const updated = {
      ...item,
      rotable: !item.rotable
    };

    setItem(updated);

    if (validateForm()) {
      setShowError(false);
      onItemChange(updated);
    }
  };

  const handleDelete = () => {
    if (readOnly) return;

    if (window.confirm(t("itemDeleteConfirm"))) {
      onDelete();
    }
  };

  const showEye = calledFrom == 1;

  const ColorSwatch = () => {
    const swatchClassName = [
      "item-color-swatch",
      onColorClick ? "is-clickable" : ""
    ]
      .filter(Boolean)
      .join(" ");

    if (!color) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{t("itemNoColor")}</Tooltip>}
        >
          <div
            className={swatchClassName}
            style={{ "--item-color-bg": "#e4e4e4" }}
            onClick={(e) => {
              e.stopPropagation();
              onColorClick?.();
            }}
          >
            <svg viewBox="0 0 10 10" className="item-color-empty-svg">
              <line
                x1="2"
                y1="2"
                x2="8"
                y2="8"
                stroke="#dc3545"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <line
                x1="8"
                y1="2"
                x2="2"
                y2="8"
                stroke="#dc3545"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </OverlayTrigger>
      );
    }

    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{t(color.name) || t("itemColor")}</Tooltip>}
      >
        <div
          className={swatchClassName}
          style={{ "--item-color-bg": color?.hex || "#e4e4e4" }}
          onClick={(e) => {
            e.stopPropagation();
            onColorClick?.();
          }}
        >
          {color.imageDataReduced && (
            <img
              src={`data:image/jpeg;base64,${color.imageDataReduced}`}
              alt={t(color.name) || ""}
              className="item-color-image"
            />
          )}
        </div>
      </OverlayTrigger>
    );
  };

  const RotateBtn = () => (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip>
          {item.rotable ? t("itemRotatable") : t("itemNotRotatable")}
        </Tooltip>
      }
    >
      <span
        role="button"
        tabIndex={0}
        onClick={handleRotate}
        className="item-rotate-btn"
      >
        <IonIcon
          icon={reload}
          className={`item-rotate-icon ${
            item.rotable ? "is-active" : "is-inactive"
          }`}
        />

        {!item.rotable && <span className="item-rotate-slash" />}
      </span>
    </OverlayTrigger>
  );

  return (
    <div
      className={[
        "item-row-group",
        visible ? "" : "is-hidden",
        showEye ? "item-row-group--with-eye" : "item-row-group--without-eye"
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={`item-main-row${collapsed ? "" : " is-expanded"}`}>
        <div className="item-chevron-cell">
          <IonIcon
            icon={collapsed ? chevronForward : chevronDown}
            className="item-chevron-icon"
            onClick={() => setCollapsed((v) => !v)}
          />
        </div>

        <div
          className="item-checkbox-cell"
          onClick={(e) => {
            e.stopPropagation();

            if (typeof onCheckedChange === "function") {
              onCheckedChange(!checked, e);
            }
          }}
        >
          <input
            type="checkbox"
            checked={!!checked}
            onChange={() => {}}
            onClick={(e) => e.stopPropagation()}
            className="item-checkbox"
          />
        </div>

        {showEye && (
          <div className="item-cell item-cell--eye">
            <IonIcon
              icon={visible ? eyeOutline : eyeOffOutline}
              className="item-visibility-icon"
              onClick={(e) => {
                e.stopPropagation();

                if (typeof onToggleVisibility === "function") {
                  onToggleVisibility();
                }
              }}
            />
          </div>
        )}

        <div
          className={`item-cell item-cell--swatch ${
            !showEye ? "item-cell--bordered" : ""
          }`}
        >
          <ColorSwatch />
        </div>

        <div className="item-milling-slot">
          <MillingDropdown
            millingId={item.millingId}
            millingVariantId={item.millingVariantId || ""}
            width={item.sizeArr[1]}
            height={item.sizeArr[0]}
            colorId={item.color?.colorId ?? null}
            allColors={allColors}
            allMillings={allMillings}
            disabled={readOnly}
            onChange={handleMillingChange}
          />
        </div>

        <div className="item-cell item-cell--width">
          <input
            className="item-input item-input--right"
            value={item.sizeArr[0]}
            onChange={(e) =>
              handleChange("sizeArr", [
                e.target.value,
                item.sizeArr[1],
                item.sizeArr[2]
              ])
            }
            onBlur={commitIfValid}
            disabled={readOnly}
          />
        </div>

        <div className="item-cell item-cell--height">
          <input
            className="item-input item-input--right"
            value={item.sizeArr[1]}
            onChange={(e) =>
              handleChange("sizeArr", [
                item.sizeArr[0],
                e.target.value,
                item.sizeArr[2]
              ])
            }
            onBlur={commitIfValid}
            disabled={readOnly}
          />
        </div>

        <div className="item-cell item-cell--qty">
          <input
            className="item-input item-input--center"
            value={item.qty}
            onChange={(e) => handleChange("qty", e.target.value)}
            onBlur={commitIfValid}
            disabled={readOnly}
          />
        </div>

        <div className="item-cell item-cell--fill item-name-main-cell">
          <input
            className="item-input"
            placeholder={t("itemNamePlaceholder")}
            value={item.name ? t(item.name) : ""}
            onChange={(e) => handleChange("name", e.target.value)}
            onBlur={commitIfValid}
            disabled={readOnly}
          />
        </div>
      </div>
      {!collapsed && (
        <div className="item-expanded-row">
          <div className="item-expanded-spacer item-expanded-spacer--chevron" />
          <div className="item-expanded-spacer item-expanded-spacer--checkbox" />

          {showEye && (
            <div className="item-cell item-cell--eye">
              {!readOnly && (
                <IonIcon
                  icon={trash}
                  className="item-delete-icon"
                  onClick={handleDelete}
                />
              )}
            </div>
          )}

          <div
            className={`item-cell item-cell--swatch ${
              !showEye ? "item-cell--bordered" : ""
            }`}
          >
            <RotateBtn />
          </div>

          <div className="item-cell item-name-tablet-cell">
            <input
              className="item-input"
              placeholder={t("itemNamePlaceholder")}
              value={item.name ? t(item.name) : ""}
              onChange={(e) => handleChange("name", e.target.value)}
              onBlur={commitIfValid}
              disabled={readOnly}
            />
          </div>
          <div className="item-cell item-cell--width">
            <select
              className="item-select"
              value={item.longCant}
              onChange={(e) => handleChange("longCant", e.target.value)}
              onBlur={commitIfValid}
              disabled={readOnly}
            >
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
            </select>
          </div>

          <div className="item-cell item-cell--height">
            <select
              className="item-select"
              value={item.shortCant}
              onChange={(e) => handleChange("shortCant", e.target.value)}
              onBlur={commitIfValid}
              disabled={readOnly}
            >
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
            </select>
          </div>

          <div className="item-cell item-cell--qty">
            <select
              className="item-select"
              value={item.cantType}
              onChange={(e) => handleChange("cantType", e.target.value)}
              onBlur={commitIfValid}
              disabled={readOnly}
            >
              <option value="-">-</option>
              <option value="04">04</option>
              <option value="2">2</option>
              <option value="42">42</option>
              <option value="1">1</option>
            </select>
          </div>

          <div className="item-cell item-cell--fill">
            <input
              className="item-input"
              placeholder={t("itemDetailsPlaceholder")}
              value={item.details || ""}
              onChange={(e) => handleChange("details", e.target.value)}
              onBlur={commitIfValid}
              disabled={readOnly}
            />
          </div>

          <div className="item-cell item-cell--tail" />
        </div>
      )}
      {showError && (
        <ErrorModal
          error="Please fill out all fields correctly."
          onClose={() => setShowError(false)}
        />
      )}
    </div>
  );
}

export { MillingDropdown };
